import {
  type Page_servicesUsernameQuery,
  type Page_servicesUsernameQuery$data,
} from '#app/__generated__/relay/Page_servicesUsernameQuery.graphql.js';
import { useProfileMagic } from '#app/components/ContraMagic/useProfileMagic.js';
import { Footer } from '#app/components/Footer/Footer.js';
import { PortfolioRedirect } from '#app/components/PortfolioRedirect/PortfolioRedirect.js';
import { SSRSafeRender } from '#app/components/SSRSafeRender/SSRSafeRender.js';
import { ProfileServices } from '#app/features/profile/components/ProfileServices/ProfileServices.js';
import { ProfileLayout } from '#app/features/profile/layouts/ProfileLayout.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useRegisterAnalyticEvent } from '#app/hooks/useRegisterAnalyticEvent.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { type PageProps } from '#app/types/router.js';
import { removeSearchParameter } from '#app/utilities/removeSearchParameter.js';
import { graphql, usePreloadedQuery } from '@contra/relay';
import { Suspense, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

type ProfileServicesPageProps = PageProps<
  '/@username/services',
  Page_servicesUsernameQuery
>;

const AuthenticatedVisitorRedirect = ({
  profile,
  visitor,
}: {
  readonly profile: Page_servicesUsernameQuery$data['userProfileByUsername'];
  readonly visitor: Page_servicesUsernameQuery$data['visitor'];
}) => {
  const navigation = useNavigation();
  const location = useLocation();
  const [urlSearchParameters] = useURLParameters('/@username/services');
  const redirectToPortfolio = urlSearchParameters.redirectToPortfolio;

  const { isProfileMagicInputModalOpen, openProfileMagicModal } =
    useProfileMagic({
      entrypoint: 'introModal',
      prefilledReason:
        visitor?.userAccount?.contraMagicQuestionnaireResponse
          ?.whatMakesYouUnique,
      prefilledServices:
        visitor?.userAccount?.contraMagicQuestionnaireResponse
          ?.whatServicesDoYouOffer,
      visitorCanEdit: profile?.visitorCanEdit ?? false,
    });

  const profileMagicParameter = urlSearchParameters.profileMagic;

  useEffect(() => {
    if (profileMagicParameter && !isProfileMagicInputModalOpen) {
      openProfileMagicModal();
      navigation.replace(removeSearchParameter(location.path, 'profileMagic'));
    }
  }, [profileMagicParameter]);

  if (redirectToPortfolio && profile) {
    return (
      <PortfolioRedirect
        displayUsername={profile.displayUsername}
        route="userServices"
      />
    );
  }

  return null;
};

const HybridPage = ({ preloaded }: ProfileServicesPageProps) => {
  const data = usePreloadedQuery<Page_servicesUsernameQuery>(
    graphql`
      query Page_servicesUsernameQuery($username: String!) {
        visitor {
          id
          userAccount {
            contraMagicQuestionnaireResponse {
              whatMakesYouUnique
              whatServicesDoYouOffer
            }
            ...ProfileLayout_userAccount
          }
        }
        userProfileByUsername(username: $username) {
          id
          firstName
          lastName
          displayUsername
          ...ProfileLayout_userProfile
          ...ProfileServices_userProfile
          visitorCanEdit
        }
      }
    `,
    preloaded,
  );
  const { userProfileByUsername: profile, visitor } = data;

  useRegisterAnalyticEvent(
    profile && !profile.visitorCanEdit
      ? {
          eventType: 'UserProfileView',
          input: { userProfileId: profile.id },
        }
      : undefined,
  );

  if (profile === null) {
    return null;
  }

  return (
    <>
      <Suspense>
        {visitor.userAccount ? (
          <AuthenticatedVisitorRedirect
            profile={profile}
            visitor={visitor}
          />
        ) : null}
      </Suspense>

      <Helmet>
        <title>{`Services offered by ${profile.firstName} ${profile.lastName}`}</title>
      </Helmet>

      <ProfileLayout
        accountRef={visitor.userAccount}
        userProfileRef={profile}
      >
        <ProfileServices profileKey={profile} />
      </ProfileLayout>

      <Footer />
    </>
  );
};

export const Page = ({ preloaded }: ProfileServicesPageProps) => {
  const currentUser = useCurrentUser();

  return (
    <SSRSafeRender skipServerRender={Boolean(currentUser)}>
      <HybridPage preloaded={preloaded} />
    </SSRSafeRender>
  );
};
